import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import type { Risk } from '@/types';
import { useMutation } from '@/utils/api';

const error = {
  CREATE: 'Failed to create ticket',
  ASSOCIATE: 'Failed to associate ticket',
  DISCONNECT: 'Failed to disconnect ticket',
  ADD_COMMENT: 'Failed to add comment to Jira ticket',
};

export function useCreateTicket() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to create ticket',
    mutationFn: ({
      riskKey,
      account,
      templateId,
    }: {
      riskKey: string;
      account: string;
      templateId: string;
    }) => {
      const promise = axios.post('/ticket', {
        risk: riskKey,
        account,
        templateId,
      });

      toast.promise(promise, {
        loading: 'Creating ticket...',
        success: 'Ticket created',
        error: err => {
          const message = err.message.replace(`${error.CREATE}: `, '');

          return `${error.CREATE}${message ? `: ${message}` : ''}`;
        },
      });

      return promise;
    },
  });
}

export function useAssociateTicket() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to associate ticket',
    mutationFn: ({
      riskKey,
      ticketId,
      account,
    }: {
      riskKey: string;
      ticketId: string;
      account: string;
    }) => {
      const promise = axios.post('/ticket', {
        risk: riskKey,
        id: ticketId,
        account,
      });

      toast.promise(promise, {
        loading: 'Associating ticket...',
        success: 'Vulnerability associated with ticket',
        error: err => {
          const message = err.message.replace(`${error.ASSOCIATE}: `, '');

          return `${error.ASSOCIATE}${message ? `: ${message}` : ''}`;
        },
      });

      return promise;
    },
  });
}

export function useDisconnectTicket() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to disconnect ticket',
    mutationFn: ({
      riskKey,
      provider,
      ticketId,
    }: {
      riskKey: string;
      provider: string;
      ticketId: string;
    }) => {
      const promise = axios.delete(`/ticket`, {
        data: { risk: riskKey, provider, ticketID: ticketId },
      });

      toast.promise(promise, {
        loading: 'Disconnecting ticket...',
        success: 'Ticket disconnected',
        error: 'Failed to disconnect ticket',
      });

      return promise;
    },
  });
}

export function useAddJiraComment() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to add comment to Jira ticket',
    mutationFn: ({
      ticketId,
      comment,
      account,
    }: {
      ticketId: string;
      comment: string;
      account: string;
    }) => {
      const promise = axios.post('/ticket/comment', {
        ticketId,
        comment,
        account,
      });

      toast.promise(promise, {
        loading: 'Adding comment to Jira ticket...',
        success: 'Comment added to Jira ticket',
        error: 'Failed to add comment to Jira ticket',
      });

      return promise;
    },
  });
}

export async function fetchTicket(
  axios: ReturnType<typeof useAxios>,
  riskKey: string,
  ticketId: string,
  account: string
): Promise<{ risks: Risk[] }> {
  const response = await axios.put('/ticket', {
    risk: riskKey,
    ticketID: ticketId,
    account,
  });
  return response.data;
}
